/**
 * Pages "Détail commande" et "Détail facture" pour le portail client boutique.
 * Phase 65 Livraison 2.
 *
 * - /boutique/compte/commandes/:id  → ShopOrderDetailPage
 *   Affiche le détail d'une ClientOrder du client connecté + boutons de téléchargement
 *   du BL signé (si présent) et de la facture (si présente).
 *
 * - /boutique/compte/factures/:id  → ShopInvoiceDetailPage
 *   Affiche le détail d'une facture B2B et permet de la télécharger en PDF avec QR-Bill.
 *
 * Charte visuelle = même que Shop.jsx / ShopAuth.jsx (fond #0A0A0A, doré #C1A063).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Download, FileText, Package, Receipt, AlertCircle,
} from "lucide-react";
import { pdf, Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import { shopApi, useShopAuth, formatShopAuthError } from "../lib/shopAuth";
import { COMPANY_HEADER } from "../lib/company";
import { ReorderButton } from "../components/shop-account/ReorderButton";

const COLORS = {
  bg: "#0A0A0A",
  surface: "#141414",
  primary: "#F2ECE4",
  secondary: "#A6A29C",
  muted: "#737373",
  gold: "#C1A063",
  basil: "#88A066",
  chili: "#E54D2E",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const fmtCHF = (n) => {
  const v = Number(n || 0);
  return v.toFixed(2);
};

// ========== Styles PDF partagés ==========
const pdfStyles = StyleSheet.create({
  page: { padding: 36, fontFamily: "Helvetica", fontSize: 9, color: "#1A1C1A" },
  headerRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 },
  companyBlock: { flexDirection: "row", alignItems: "flex-start", gap: 12 },
  companyLogo: { width: 56, height: 56 },
  companyInfo: { flexDirection: "column" },
  companyName: { fontSize: 18, fontFamily: "Helvetica-Bold", marginBottom: 2, letterSpacing: 0.8 },
  companyMeta: { fontSize: 8, color: "#575A57", lineHeight: 1.4 },
  rightBlock: { flexDirection: "column", alignItems: "flex-end" },
  docTitle: { fontSize: 18, fontFamily: "Helvetica-Bold", color: "#1F4E3D" },
  docRef: { fontSize: 11, marginTop: 4, color: "#575A57", fontFamily: "Helvetica-Bold" },
  docDate: { fontSize: 9, marginTop: 2, color: "#575A57" },
  parties: { flexDirection: "row", gap: 16, marginBottom: 18 },
  party: { flex: 1, padding: 12, backgroundColor: "#FAFAF7", borderRadius: 4 },
  partyLabel: { fontSize: 8, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 },
  partyName: { fontSize: 11, fontFamily: "Helvetica-Bold", marginBottom: 2 },
  partyText: { fontSize: 9, color: "#575A57", marginBottom: 1 },
  table: { borderTop: "1pt solid #E5E5E0", borderLeft: "1pt solid #E5E5E0", borderRight: "1pt solid #E5E5E0", marginBottom: 16 },
  th: { flexDirection: "row", backgroundColor: "#F4F4F0", padding: 6, borderBottom: "1pt solid #E5E5E0" },
  thCell: { fontFamily: "Helvetica-Bold", fontSize: 8, color: "#575A57", textTransform: "uppercase", letterSpacing: 0.5 },
  tr: { flexDirection: "row", padding: 6, borderBottom: "1pt solid #E5E5E0" },
  td: { fontSize: 9 },
  totals: { marginTop: 12, alignSelf: "flex-end", width: "45%" },
  totalRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 3 },
  totalLabel: { fontSize: 9, color: "#575A57" },
  totalValue: { fontSize: 9, fontFamily: "Helvetica-Bold" },
  totalGrand: { flexDirection: "row", justifyContent: "space-between", paddingTop: 6, marginTop: 4, borderTop: "1pt solid #1A1C1A" },
  notes: { marginTop: 14, padding: 10, backgroundColor: "#FAFAF7", borderLeft: "3pt solid #1F4E3D" },
  notesLabel: { fontSize: 8, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3 },
  footer: { position: "absolute", bottom: 24, left: 36, right: 36, fontSize: 7, color: "#A3A6A3", textAlign: "center", borderTop: "0.5pt solid #E5E5E0", paddingTop: 6 },
});

const CompanyHeader = ({ settings, docTitle, docRef, docDate }) => (
  <View style={pdfStyles.headerRow}>
    <View style={pdfStyles.companyBlock}>
      <Image src={`${window.location.origin}/logo-maisonthai-pdf.png`} style={pdfStyles.companyLogo} />
      <View style={pdfStyles.companyInfo}>
        <Image
          src={`${typeof window !== "undefined" ? window.location.origin : ""}/company-name-sayonara.png`}
          style={{ width: 179, height: 18, marginBottom: 2 }}
        />
        {(settings?.company_address || "").split("\n").filter(Boolean).map((line, i) => (
          <Text key={i} style={pdfStyles.companyMeta}>{line}</Text>
        ))}
        {settings?.company_postal_code && settings?.company_city && (
          <Text style={pdfStyles.companyMeta}>{settings.company_postal_code} {settings.company_city}</Text>
        )}
        {settings?.company_phone && <Text style={pdfStyles.companyMeta}>Tél : {settings.company_phone}</Text>}
        {settings?.company_email && <Text style={pdfStyles.companyMeta}>{settings.company_email}</Text>}
        {settings?.company_vat_number && <Text style={pdfStyles.companyMeta}>TVA : {settings.company_vat_number}</Text>}
      </View>
    </View>
    <View style={pdfStyles.rightBlock}>
      <Text style={pdfStyles.docTitle}>{docTitle}</Text>
      <Text style={pdfStyles.docRef}>N° {docRef}</Text>
      <Text style={pdfStyles.docDate}>{docDate}</Text>
    </View>
  </View>
);

// ========== PDF : BL (bon de livraison) ==========
const BLPdfDoc = ({ bl, settings }) => {
  const lines = bl.lines || [];
  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <CompanyHeader
          settings={settings}
          docTitle="BON DE LIVRAISON"
          docRef={bl.reference}
          docDate={`Livraison : ${fmtDate(bl.delivery_date)}`}
        />

        <View style={pdfStyles.parties}>
          <View style={pdfStyles.party}>
            <Text style={pdfStyles.partyLabel}>Émetteur</Text>
            <Text style={pdfStyles.partyName}>{COMPANY_HEADER}</Text>
            {bl.from_distributor_name && <Text style={pdfStyles.partyText}>Dépôt : {bl.from_distributor_name}</Text>}
          </View>
          <View style={pdfStyles.party}>
            <Text style={pdfStyles.partyLabel}>Destinataire</Text>
            {/* Format suisse — Société / Contact / Rue / NPA Localité */}
            <Text style={pdfStyles.partyName}>{bl.distributor_name || "—"}</Text>
            {bl.distributor_contact ? <Text style={pdfStyles.partyText}>{bl.distributor_contact}</Text> : null}
            {String(bl.distributor_address || "")
              .split(/\n|,\s/)
              .map((s) => s.trim())
              .filter(Boolean)
              .map((line, i) => (
                <Text key={i} style={pdfStyles.partyText}>{line}</Text>
              ))}
          </View>
        </View>

        <View style={pdfStyles.table}>
          <View style={pdfStyles.th}>
            <Text style={[pdfStyles.thCell, { flex: 3 }]}>Produit</Text>
            <Text style={[pdfStyles.thCell, { flex: 0.8 }]}>SKU</Text>
            <Text style={[pdfStyles.thCell, { flex: 0.7, textAlign: "right" }]}>Qté</Text>
            <Text style={[pdfStyles.thCell, { flex: 0.9, textAlign: "right" }]}>DLC</Text>
          </View>
          {lines.map((ln, idx) => (
            <View key={idx} style={pdfStyles.tr}>
              <Text style={[pdfStyles.td, { flex: 3 }]}>{ln.product_name || "—"}</Text>
              <Text style={[pdfStyles.td, { flex: 0.8 }]}>{ln.product_sku || "—"}</Text>
              <Text style={[pdfStyles.td, { flex: 0.7, textAlign: "right" }]}>{ln.quantity}</Text>
              <Text style={[pdfStyles.td, { flex: 0.9, textAlign: "right" }]}>{ln.dlc ? fmtDate(ln.dlc) : "—"}</Text>
            </View>
          ))}
        </View>

        {bl.notes && (
          <View style={pdfStyles.notes}>
            <Text style={pdfStyles.notesLabel}>Notes</Text>
            <Text>{bl.notes}</Text>
          </View>
        )}

        <Text style={pdfStyles.footer}>
          Bon de livraison — la DLC fait foi pour la péremption. Traçabilité HACCP par lot disponible côté émetteur.
        </Text>
      </Page>
    </Document>
  );
};

// ========== PDF : Facture ==========
const InvoicePdfDoc = ({ invoice, settings, orderReference }) => {
  const lines = invoice.lines || [];
  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <CompanyHeader
          settings={settings}
          docTitle="FACTURE"
          docRef={invoice.reference}
          docDate={`Émise le ${fmtDate(invoice.issue_date)}`}
        />

        <View style={pdfStyles.parties}>
          <View style={pdfStyles.party}>
            <Text style={pdfStyles.partyLabel}>Émetteur</Text>
            <Text style={pdfStyles.partyName}>{COMPANY_HEADER}</Text>
            {invoice.creditor_address && <Text style={pdfStyles.partyText}>{invoice.creditor_address}</Text>}
            {(invoice.creditor_postal_code || invoice.creditor_city) && (
              <Text style={pdfStyles.partyText}>{invoice.creditor_postal_code} {invoice.creditor_city}</Text>
            )}
            {invoice.creditor_iban && <Text style={pdfStyles.partyText}>IBAN : {invoice.creditor_iban}</Text>}
            {invoice.creditor_vat_number && <Text style={pdfStyles.partyText}>TVA : {invoice.creditor_vat_number}</Text>}
          </View>
          <View style={pdfStyles.party}>
            <Text style={pdfStyles.partyLabel}>Débiteur</Text>
            <Text style={pdfStyles.partyName}>{invoice.debtor_name || "—"}</Text>
            {invoice.debtor_address && <Text style={pdfStyles.partyText}>{invoice.debtor_address}</Text>}
            {(invoice.debtor_postal_code || invoice.debtor_city) && (
              <Text style={pdfStyles.partyText}>{invoice.debtor_postal_code} {invoice.debtor_city}</Text>
            )}
            {orderReference && <Text style={pdfStyles.partyText}>Commande : {orderReference}</Text>}
          </View>
        </View>

        <View style={pdfStyles.table}>
          <View style={pdfStyles.th}>
            <Text style={[pdfStyles.thCell, { flex: 3.5 }]}>Description</Text>
            <Text style={[pdfStyles.thCell, { flex: 0.7, textAlign: "right" }]}>Qté</Text>
            <Text style={[pdfStyles.thCell, { flex: 0.6 }]}>Unité</Text>
            <Text style={[pdfStyles.thCell, { flex: 0.9, textAlign: "right" }]}>PU CHF</Text>
            <Text style={[pdfStyles.thCell, { flex: 0.6, textAlign: "right" }]}>TVA %</Text>
            <Text style={[pdfStyles.thCell, { flex: 1, textAlign: "right" }]}>Total CHF</Text>
          </View>
          {lines.map((ln, idx) => {
            const total = (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0);
            return (
              <View key={idx} style={pdfStyles.tr}>
                <Text style={[pdfStyles.td, { flex: 3.5 }]}>{ln.description || "—"}</Text>
                <Text style={[pdfStyles.td, { flex: 0.7, textAlign: "right" }]}>{ln.quantity}</Text>
                <Text style={[pdfStyles.td, { flex: 0.6 }]}>{ln.unit || "pcs"}</Text>
                <Text style={[pdfStyles.td, { flex: 0.9, textAlign: "right" }]}>{fmtCHF(ln.unit_price)}</Text>
                <Text style={[pdfStyles.td, { flex: 0.6, textAlign: "right" }]}>{ln.vat_rate}</Text>
                <Text style={[pdfStyles.td, { flex: 1, textAlign: "right" }]}>{fmtCHF(total)}</Text>
              </View>
            );
          })}
        </View>

        <View style={pdfStyles.totals}>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Total HT</Text>
            <Text style={pdfStyles.totalValue}>{fmtCHF(invoice.total_ht)} CHF</Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>TVA</Text>
            <Text style={pdfStyles.totalValue}>{fmtCHF(invoice.total_vat)} CHF</Text>
          </View>
          <View style={pdfStyles.totalGrand}>
            <Text style={[pdfStyles.totalLabel, { fontSize: 11 }]}>TOTAL TTC</Text>
            <Text style={[pdfStyles.totalValue, { fontSize: 11 }]}>{fmtCHF(invoice.total_ttc)} CHF</Text>
          </View>
        </View>

        {invoice.payment_terms && (
          <View style={pdfStyles.notes}>
            <Text style={pdfStyles.notesLabel}>Conditions de paiement</Text>
            <Text>{invoice.payment_terms}</Text>
            {invoice.due_date && <Text style={{ marginTop: 4 }}>Échéance : {fmtDate(invoice.due_date)}</Text>}
            {invoice.qr_reference && <Text style={{ marginTop: 4 }}>Référence : {invoice.qr_reference}</Text>}
          </View>
        )}

        {invoice.additional_info && (
          <View style={pdfStyles.notes}>
            <Text style={pdfStyles.notesLabel}>Informations</Text>
            <Text>{invoice.additional_info}</Text>
          </View>
        )}

        <Text style={pdfStyles.footer}>
          Document généré par {COMPANY_HEADER} — facture conforme à la législation suisse.
        </Text>
      </Page>
    </Document>
  );
};

// ========== Utility : déclenche le téléchargement d'un blob PDF ==========
async function downloadPdf(document, filename) {
  const blob = await pdf(document).toBlob();
  const url = URL.createObjectURL(blob);
  const a = window.document.createElement("a");
  a.href = url;
  a.download = filename;
  window.document.body.appendChild(a);
  a.click();
  window.document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ========== Bouton primaire (cohérent avec ShopAuth) ==========
function PrimaryAction({ children, onClick, loading, icon: Icon, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="inline-flex items-center justify-center gap-2 px-5 py-3 font-body uppercase tracking-[0.2em] text-xs transition-all disabled:opacity-50"
      style={{
        background: COLORS.gold,
        color: COLORS.bg,
        border: `1px solid ${COLORS.gold}`,
        fontWeight: 600,
      }}
      data-testid={testid}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : Icon ? <Icon className="w-4 h-4" /> : null}
      {children}
    </button>
  );
}

// ============================ Page : Détail commande ============================
export function ShopOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customer } = useShopAuth();

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [downloadingBL, setDownloadingBL] = useState(false);
  const [downloadingInv, setDownloadingInv] = useState(false);

  useEffect(() => {
    if (customer === false) navigate("/boutique/login", { replace: true });
  }, [customer, navigate]);

  useEffect(() => {
    if (!customer || customer === false) return;
    shopApi
      .get(`/my-orders/${id}/detail`)
      .then(({ data }) => setData(data))
      .catch((err) => {
        setError(formatShopAuthError(err.response?.data?.detail) || err.message);
        setData(false);
      });
  }, [id, customer]);

  const onDownloadBL = async () => {
    if (!data?.delivery_note) return;
    setDownloadingBL(true);
    try {
      await downloadPdf(
        <BLPdfDoc bl={data.delivery_note} settings={data.settings} />,
        `BL-${data.delivery_note.reference || data.order.reference}.pdf`,
      );
    } finally {
      setDownloadingBL(false);
    }
  };

  const onDownloadInvoice = async () => {
    if (!data?.invoice) return;
    setDownloadingInv(true);
    try {
      await downloadPdf(
        <InvoicePdfDoc invoice={data.invoice} settings={data.settings} orderReference={data.order.reference} />,
        `Facture-${data.invoice.reference || data.order.reference}.pdf`,
      );
    } finally {
      setDownloadingInv(false);
    }
  };

  if (customer === null || data === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="order-detail-loading">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: COLORS.gold }} />
      </div>
    );
  }
  if (customer === false) return null;

  if (data === false || !data?.order) {
    return (
      <section className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12 py-16">
        <Link to="/boutique/compte" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] mb-6" style={{ color: COLORS.secondary }}>
          <ArrowLeft className="w-3.5 h-3.5" /> Retour à mon compte
        </Link>
        <div className="p-6" style={{ background: "rgba(229,77,46,0.08)", border: "1px solid rgba(229,77,46,0.35)" }} data-testid="order-detail-error">
          <p style={{ color: COLORS.chili }} className="text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error || "Commande introuvable."}
          </p>
        </div>
      </section>
    );
  }

  const order = data.order;
  const bl = data.delivery_note;
  const invoice = data.invoice;

  const productLines = (order.lines || []).filter((l) => l.product_id !== "__shipping__");
  const shippingLine = (order.lines || []).find((l) => l.product_id === "__shipping__");
  const subtotal = productLines.reduce((s, l) => s + (Number(l.unit_price) || 0) * (Number(l.quantity) || 0), 0);
  const shipping = shippingLine ? (Number(shippingLine.unit_price) || 0) * (Number(shippingLine.quantity) || 0) : 0;
  const total = subtotal + shipping;

  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 py-12 md:py-16" data-testid="order-detail-page">
      <Link to="/boutique/compte" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] mb-6" style={{ color: COLORS.secondary }}>
        <ArrowLeft className="w-3.5 h-3.5" /> Retour à mon compte
      </Link>

      <div className="mb-8">
        <p className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: COLORS.gold }}>Commande</p>
        <h1 className="text-2xl md:text-3xl font-mono mb-2" style={{ color: COLORS.primary }}>{order.reference}</h1>
        <p className="text-sm" style={{ color: COLORS.secondary }}>
          Passée le {fmtDate(order.created_at)} · Statut : <span style={{ color: COLORS.primary }}>{order.status}</span>
        </p>
      </div>

      <div className="mb-4 flex justify-end">
        <ReorderButton lines={data.order.lines} testid="order-detail-reorder" />
      </div>

      {/* Boutons téléchargement */}
      {(bl || invoice) && (
        <div className="mb-8 flex flex-wrap gap-3" data-testid="order-detail-downloads">
          {bl && (
            <PrimaryAction onClick={onDownloadBL} loading={downloadingBL} icon={Package} testid="download-bl-btn">
              {downloadingBL ? "Génération…" : "Télécharger le bon de livraison"}
            </PrimaryAction>
          )}
          {invoice && (
            <PrimaryAction onClick={onDownloadInvoice} loading={downloadingInv} icon={Receipt} testid="download-invoice-btn">
              {downloadingInv ? "Génération…" : "Télécharger la facture"}
            </PrimaryAction>
          )}
        </div>
      )}

      {/* Lignes de la commande */}
      <div style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)" }} className="p-6 mb-6">
        <h2 className="text-base uppercase tracking-[0.2em] mb-4" style={{ color: COLORS.gold }}>Articles</h2>
        <ul className="divide-y" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
          {productLines.map((ln) => (
            <li key={ln.id || ln.product_id} className="py-3 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm" style={{ color: COLORS.primary }}>{ln.product_name}</p>
                <p className="text-xs mt-0.5" style={{ color: COLORS.muted }}>
                  {ln.quantity} × {fmtCHF(ln.unit_price)} CHF
                  {ln.product_sku && <> · <span className="font-mono">{ln.product_sku}</span></>}
                </p>
              </div>
              <p className="font-mono text-sm shrink-0" style={{ color: COLORS.primary }}>
                {fmtCHF((Number(ln.unit_price) || 0) * (Number(ln.quantity) || 0))} CHF
              </p>
            </li>
          ))}
        </ul>
        <div className="mt-4 pt-4 space-y-1.5" style={{ borderTop: "1px solid rgba(242,236,228,0.08)" }}>
          <div className="flex justify-between text-xs" style={{ color: COLORS.secondary }}>
            <span>Sous-total</span>
            <span className="font-mono">{fmtCHF(subtotal)} CHF</span>
          </div>
          {shippingLine && (
            <div className="flex justify-between text-xs" style={{ color: COLORS.secondary }}>
              <span>{shippingLine.product_name}</span>
              <span className="font-mono">{fmtCHF(shipping)} CHF</span>
            </div>
          )}
          <div className="flex justify-between pt-2 mt-2 text-sm" style={{ borderTop: "1px solid rgba(242,236,228,0.08)" }}>
            <span style={{ color: COLORS.primary }}>Total</span>
            <span className="font-mono" style={{ color: COLORS.gold }}>{fmtCHF(total)} CHF</span>
          </div>
        </div>
      </div>

      {/* Statuts BL / Facture */}
      <div className="grid md:grid-cols-2 gap-4">
        <div style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)" }} className="p-5">
          <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: COLORS.gold }}>Livraison</p>
          {bl ? (
            <>
              <p className="text-sm font-mono mt-2" style={{ color: COLORS.primary }}>{bl.reference}</p>
              <p className="text-xs mt-1" style={{ color: COLORS.secondary }}>
                {bl.signed_at ? <>Signé le {fmtDate(bl.signed_at)} par {bl.signed_by || "—"}</> : <>Statut : {bl.status}</>}
              </p>
            </>
          ) : (
            <p className="text-xs mt-2" style={{ color: COLORS.muted }}>
              Aucun bon de livraison émis pour le moment.
            </p>
          )}
        </div>
        <div style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)" }} className="p-5">
          <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: COLORS.gold }}>Facturation</p>
          {invoice ? (
            <>
              <p className="text-sm font-mono mt-2" style={{ color: COLORS.primary }}>{invoice.reference}</p>
              <p className="text-xs mt-1" style={{ color: COLORS.secondary }}>
                Statut : {invoice.status} · {fmtCHF(invoice.total_ttc)} CHF
              </p>
              <Link
                to={`/boutique/compte/factures/${invoice.id}`}
                className="text-xs uppercase tracking-[0.2em] underline inline-flex items-center gap-1 mt-2"
                style={{ color: COLORS.gold }}
                data-testid="link-to-invoice-detail"
              >
                <FileText className="w-3 h-3" /> Voir la facture
              </Link>
            </>
          ) : (
            <p className="text-xs mt-2" style={{ color: COLORS.muted }}>
              Aucune facture émise (paiement en ligne).
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

// ============================ Page : Détail facture ============================
export function ShopInvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customer } = useShopAuth();

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (customer === false) navigate("/boutique/login", { replace: true });
  }, [customer, navigate]);

  useEffect(() => {
    if (!customer || customer === false) return;
    shopApi
      .get(`/my-invoices/${id}/detail`)
      .then(({ data }) => setData(data))
      .catch((err) => {
        setError(formatShopAuthError(err.response?.data?.detail) || err.message);
        setData(false);
      });
  }, [id, customer]);

  const onDownload = async () => {
    if (!data?.invoice) return;
    setDownloading(true);
    try {
      await downloadPdf(
        <InvoicePdfDoc invoice={data.invoice} settings={data.settings} orderReference={data.order_reference} />,
        `Facture-${data.invoice.reference || data.invoice.id}.pdf`,
      );
    } finally {
      setDownloading(false);
    }
  };

  if (customer === null || data === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="invoice-detail-loading">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: COLORS.gold }} />
      </div>
    );
  }
  if (customer === false) return null;

  if (data === false || !data?.invoice) {
    return (
      <section className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12 py-16">
        <Link to="/boutique/compte" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] mb-6" style={{ color: COLORS.secondary }}>
          <ArrowLeft className="w-3.5 h-3.5" /> Retour à mon compte
        </Link>
        <div className="p-6" style={{ background: "rgba(229,77,46,0.08)", border: "1px solid rgba(229,77,46,0.35)" }} data-testid="invoice-detail-error">
          <p style={{ color: COLORS.chili }} className="text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error || "Facture introuvable."}
          </p>
        </div>
      </section>
    );
  }

  const inv = data.invoice;
  const due = (Number(inv.total_ttc) || 0) - (Number(inv.paid_amount) || 0);

  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 py-12 md:py-16" data-testid="invoice-detail-page">
      <Link to="/boutique/compte" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] mb-6" style={{ color: COLORS.secondary }}>
        <ArrowLeft className="w-3.5 h-3.5" /> Retour à mon compte
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: COLORS.gold }}>Facture</p>
          <h1 className="text-2xl md:text-3xl font-mono mb-2" style={{ color: COLORS.primary }}>{inv.reference}</h1>
          <p className="text-sm" style={{ color: COLORS.secondary }}>
            Émise le {fmtDate(inv.issue_date)}
            {inv.due_date && <> · Échéance {fmtDate(inv.due_date)}</>}
            {data.order_reference && <> · Commande <span className="font-mono">{data.order_reference}</span></>}
          </p>
        </div>
        <PrimaryAction onClick={onDownload} loading={downloading} icon={Download} testid="invoice-download-btn">
          {downloading ? "Génération…" : "Télécharger le PDF"}
        </PrimaryAction>
      </div>

      {/* Statut & montant */}
      <div
        className="p-5 mb-6 flex items-center justify-between flex-wrap gap-4"
        style={{
          background: inv.status === "PAYEE" ? "rgba(136,160,102,0.08)" : "rgba(193,160,99,0.08)",
          border: `1px solid ${inv.status === "PAYEE" ? COLORS.basil : COLORS.gold}`,
        }}
        data-testid="invoice-status-banner"
      >
        <div>
          <p className="text-xs uppercase tracking-[0.2em]" style={{ color: inv.status === "PAYEE" ? COLORS.basil : COLORS.gold }}>
            {inv.status === "PAYEE" ? "Facture payée" : inv.status === "EN_RETARD" ? "En retard" : "À régler"}
          </p>
          <p className="text-2xl mt-1 font-mono" style={{ color: COLORS.primary }}>
            {fmtCHF(inv.status === "PAYEE" ? inv.total_ttc : due)} <span className="text-sm" style={{ color: COLORS.secondary }}>CHF</span>
          </p>
        </div>
        {inv.qr_reference && (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: COLORS.secondary }}>Référence</p>
            <p className="font-mono text-xs mt-1" style={{ color: COLORS.primary }}>{inv.qr_reference}</p>
          </div>
        )}
      </div>

      {/* Détail lignes */}
      <div style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)" }} className="p-6">
        <h2 className="text-base uppercase tracking-[0.2em] mb-4" style={{ color: COLORS.gold }}>Détail</h2>
        <ul className="divide-y" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
          {(inv.lines || []).map((ln) => {
            const total = (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0);
            return (
              <li key={ln.id} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm" style={{ color: COLORS.primary }}>{ln.description}</p>
                  <p className="text-xs mt-0.5" style={{ color: COLORS.muted }}>
                    {ln.quantity} × {fmtCHF(ln.unit_price)} CHF · TVA {ln.vat_rate}%
                  </p>
                </div>
                <p className="font-mono text-sm shrink-0" style={{ color: COLORS.primary }}>{fmtCHF(total)} CHF</p>
              </li>
            );
          })}
        </ul>
        <div className="mt-4 pt-4 space-y-1.5" style={{ borderTop: "1px solid rgba(242,236,228,0.08)" }}>
          <div className="flex justify-between text-xs" style={{ color: COLORS.secondary }}>
            <span>Total HT</span>
            <span className="font-mono">{fmtCHF(inv.total_ht)} CHF</span>
          </div>
          <div className="flex justify-between text-xs" style={{ color: COLORS.secondary }}>
            <span>TVA</span>
            <span className="font-mono">{fmtCHF(inv.total_vat)} CHF</span>
          </div>
          <div className="flex justify-between pt-2 mt-2 text-sm" style={{ borderTop: "1px solid rgba(242,236,228,0.08)" }}>
            <span style={{ color: COLORS.primary }}>Total TTC</span>
            <span className="font-mono" style={{ color: COLORS.gold }}>{fmtCHF(inv.total_ttc)} CHF</span>
          </div>
        </div>
      </div>

      {inv.payment_terms && (
        <div className="mt-6 p-5" style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)" }}>
          <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: COLORS.gold }}>Conditions de paiement</p>
          <p className="text-sm mt-2" style={{ color: COLORS.primary }}>{inv.payment_terms}</p>
        </div>
      )}
    </section>
  );
}
